import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, RotateCw, ShoppingCart } from 'lucide-react';

import { ProductCard } from '@/components/catalog/ProductCard';
import { useCart } from '@/hooks/useCart';
import { useCatalog } from '@/hooks/useCatalog';
import { useCustomerPricing, priceFor, pricingPending } from '@/hooks/useCustomerPricing';
import { useOrderHistory } from '@/hooks/useOrderHistory';

/**
 * OrderAgainCard — home dashboard surface that lets the customer one-tap
 * re-add their most-ordered items to the cart. Pulls from useOrderHistory
 * (aggregates orders.order_lines) ranked by order frequency, joined with
 * the catalog for image + display name + customer-specific price.
 *
 * Renders with the exact same `ProductCard` component the regular Order
 * screen uses so the visual language stays identical — compact row with
 * small image, name, price, qty controls.
 */
export function OrderAgainCard() {
  const navigate = useNavigate();
  const { data: items = [], isLoading: catalogLoading } = useCatalog();
  const { data: history, isLoading: historyLoading } = useOrderHistory();
  const { data: pricing } = useCustomerPricing();
  const { cart, setQty, adjustQty } = useCart();

  const top = useMemo(() => {
    const freq = history?.byCatalogItemId ?? new Map();
    return items
      .filter((i) => freq.has(i.id))
      .sort((a, b) => (freq.get(b.id)?.orderCount ?? 0) - (freq.get(a.id)?.orderCount ?? 0))
      .slice(0, 6);
  }, [items, history]);

  const loading = catalogLoading || historyLoading;

  // Don't render the section at all when there's nothing to show — keeps
  // the Home dashboard from looking like a stub for new customers.
  if (!loading && top.length === 0) return null;

  return (
    <div className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep">
      <div className="bg-brix-primary-deep px-6 md:px-8 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0">
            <RotateCw size={18} />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-bold tracking-tight text-white leading-none">
              Order again
            </h2>
            <p className="text-xs text-white/85 tracking-tight mt-1">
              Your most-ordered items — one tap to add to cart.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/order')}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 text-white font-semibold text-xs tracking-tight hover:bg-white/25"
        >
          Full catalog <ArrowRight size={12} />
        </button>
      </div>
      <div className="px-6 md:px-8 pt-5 pb-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-900 tracking-tight">
            <Loader2 size={14} className="animate-spin" /> Loading your regulars…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {top.map((item) => {
              const { unitPrice, isCustom } = priceFor(item, pricing);
              return (
                <ProductCard
                  key={item.id}
                  item={item}
                  unitPrice={unitPrice}
                  isCustomPrice={isCustom}
                  pricePending={pricingPending(pricing)}
                  qty={cart[item.id] ?? 0}
                  onAdjust={adjustQty}
                  onSet={setQty}
                />
              );
            })}
          </div>
        )}

        {!loading && top.length > 0 && (
          <div className="mt-4 flex items-center justify-end">
            <button
              type="button"
              onClick={() => navigate('/cart')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brix-primary-deep text-white text-sm font-semibold tracking-tight hover:bg-brix-primary transition-colors cursor-pointer"
            >
              <ShoppingCart size={13} /> Review cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
